/**
 * PaymentRequest v1 — compact, self-contained binary codec for payment-request links.
 * Encodes { chainId, to, tokenId, amount, note } into a Base64URL blob for the URL.
 * No database — everything travels in the link.
 *
 *   ┌─────────┬───────┬─────────┬───────────┬──────────┬───────┬────────┬────────┬─────────┬──────┬───────┐
 *   │ version │ flags │ chainId │ recipient │ tokenLen │ token │ amtLen │ amount │ noteLen │ note │ crc32 │
 *   │ 1 (=1)  │ 1     │ 4  u32  │ 20 bytes  │ 1        │ N u8  │ 1      │ M u8   │ 2  u16  │ K    │ 4     │
 *   └─────────┴───────┴─────────┴───────────┴──────────┴───────┴────────┴────────┴─────────┴──────┴───────┘
 *
 * Big-endian. chainId = canonical EVM chain-id (no dictionary to keep in sync).
 * amount = exact decimal STRING (money-safe: no base-unit/precision conversion).
 * crc32 = corruption check (fail-closed) — NOT a signature. This is ENCODING, not encryption.
 */

export const VERSION = 1;
const HEADER = 6; // version(1) + flags(1) + chainId(4)
const ADDR = 20;
const CRC = 4;

// ── Base64URL (browser btoa/atob first; Buffer fallback for Node tooling) ──
export function bytesToBase64Url(bytes) {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let bin = "";
  for (let i = 0; i < u8.length; i++) bin += String.fromCharCode(u8[i]);
  const b64 = typeof btoa !== "undefined" ? btoa(bin) : Buffer.from(u8).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function base64UrlToBytes(str) {
  let t = String(str).replace(/-/g, "+").replace(/_/g, "/");
  while (t.length % 4) t += "=";
  const bin = typeof atob !== "undefined" ? atob(t) : Buffer.from(t, "base64").toString("binary");
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// ── CRC-32 (IEEE, reflected, poly 0xEDB88320) — integrity only, not a MAC ──
let CRC_TABLE = null;
function crcTable() {
  if (CRC_TABLE) return CRC_TABLE;
  CRC_TABLE = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    CRC_TABLE[n] = c >>> 0;
  }
  return CRC_TABLE;
}
export function crc32(bytes) {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = (c >>> 8) ^ t[(c ^ bytes[i]) & 0xff];
  return (c ^ 0xffffffff) >>> 0;
}

function hexTo20(addr) {
  const m = /^0x([0-9a-fA-F]{40})$/.exec(String(addr).trim());
  if (!m) throw new Error("invalid address (expected 0x + 40 hex)");
  const out = new Uint8Array(20);
  for (let i = 0; i < 20; i++) out[i] = parseInt(m[1].substr(i * 2, 2), 16);
  return out;
}
function bytesToHex(b) {
  let s = "";
  for (let i = 0; i < b.length; i++) s += b[i].toString(16).padStart(2, "0");
  return s;
}

export function encodePaymentRequest({ chainId, to, tokenId, amount, note = "" }) {
  const te = new TextEncoder();
  const addr = hexTo20(to);
  const tok = te.encode(String(tokenId));
  const amt = te.encode(String(amount));
  const nt = te.encode(String(note));
  if (!Number.isInteger(chainId) || chainId < 0 || chainId > 0xffffffff)
    throw new Error("chainId out of uint32 range");
  if (tok.length < 1 || tok.length > 255) throw new Error("token length out of range");
  if (amt.length < 1 || amt.length > 255) throw new Error("amount length out of range");
  if (nt.length > 0xffff) throw new Error("note too long");

  const bodyLen = HEADER + ADDR + 1 + tok.length + 1 + amt.length + 2 + nt.length;
  const buf = new Uint8Array(bodyLen + CRC);
  const dv = new DataView(buf.buffer);
  let p = 0;
  buf[p++] = VERSION;
  buf[p++] = 0; // flags (reserved)
  dv.setUint32(p, chainId >>> 0, false); p += 4;
  buf.set(addr, p); p += ADDR;
  buf[p++] = tok.length; buf.set(tok, p); p += tok.length;
  buf[p++] = amt.length; buf.set(amt, p); p += amt.length;
  dv.setUint16(p, nt.length, false); p += 2; buf.set(nt, p); p += nt.length;
  dv.setUint32(p, crc32(buf.subarray(0, bodyLen)), false); // p === bodyLen
  return bytesToBase64Url(buf);
}

export function decodePaymentRequest(blob) {
  const buf = base64UrlToBytes(blob);
  const min = HEADER + ADDR + 1 + 1 + 1 + 1 + 2 + CRC;
  if (buf.length < min) throw new Error("payload too short");
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const bodyLen = buf.length - CRC;
  if (crc32(buf.subarray(0, bodyLen)) !== dv.getUint32(bodyLen, false))
    throw new Error("checksum mismatch (link corrupted or truncated)");

  let p = 0;
  const version = buf[p++];
  if (version !== VERSION) throw new Error("unsupported version " + version);
  const flags = buf[p++];
  const chainId = dv.getUint32(p, false); p += 4;
  const to = "0x" + bytesToHex(buf.subarray(p, p + ADDR)); p += ADDR;

  const need = (n) => { if (p + n > bodyLen) throw new Error("malformed payload"); };
  need(1); const tl = buf[p++]; need(tl);
  const tokenId = new TextDecoder().decode(buf.subarray(p, p + tl)); p += tl;
  need(1); const al = buf[p++]; need(al);
  const amount = new TextDecoder().decode(buf.subarray(p, p + al)); p += al;
  need(2); const nl = dv.getUint16(p, false); p += 2; need(nl);
  const note = nl ? new TextDecoder().decode(buf.subarray(p, p + nl)) : ""; p += nl;
  if (p !== bodyLen) throw new Error("trailing bytes in payload");

  return { version, flags, chainId, to, tokenId, amount, note };
}
